import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';

// The caller's most recent scans — enough to answer "where am I, and did I move?"
//
// WHY THIS EXISTS
// The product had no memory. Signing back in dropped you on the scanner showing
// the same first-run explainer you saw on day one: no last score, no progress,
// no reason to have come back. 1.16 sessions per user, 4.2% 30-day return.
// ScanRevealModal has supported a "your score moved" line since it was written
// and Create.js passed it `null`, so it has never once been shown to anyone.
//
// One callable serves three consumers: the reveal delta, the returning-member
// summary, and Home's score trend.
//
// The default is 2 — latest and previous is all the first two read, and
// listVaultEntries signs a Storage URL per entry, so over-fetching costs real
// signing calls on every mount for data nothing displays. Home overrides it
// because the trend chart genuinely plots every point it asks for; that is the
// bar for raising this, not "more might be useful later".
const ENTRIES_TO_FETCH = 2;

export default function useScanHistory({ enabled = true, limit = ENTRIES_TO_FETCH } = {}) {
  const [state, setState] = useState({ phase: 'idle', entries: [] });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: 'loading' }));
    try {
      const listVaultEntries = httpsCallable(functions, 'listVaultEntries');
      const res = await listVaultEntries({ limit });
      const entries = (res && res.data && res.data.entries) || [];
      setState({ phase: 'ready', entries });
    } catch (err) {
      // Non-fatal by design. This drives an encouragement surface, not a gate —
      // a failed history read must never stop someone scanning.
      console.error('useScanHistory: load failed', err);
      setState({ phase: 'error', entries: [] });
    }
  }, [limit]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  const entries = state.entries;
  return {
    phase: state.phase,
    entries,
    latest: entries[0] || null,
    previous: entries[1] || null,
    hasScans: entries.length > 0,
    reload: load,
  };
}
